// Error handling utilities for consistent error management across the application
// Provides functions for extracting user-friendly error messages and handling common error patterns

/**
 * Extracts a user-friendly error message from an error object.
 * Handles axios error structure, backend error responses, and generic Error objects.
 * 
 * @param {Error|Object} error - Error object, axios error, or any throwable
 * @param {Object} options - Configuration options
 * @param {string} options.defaultMessage - Default message if no meaningful message can be extracted
 * @param {boolean} options.includeStatusCode - Whether to include HTTP status code in message
 * @returns {string} User-friendly error message
 */
export const extractErrorMessage = (error, options = {}) => {
    const {
        defaultMessage = 'Ein unerwarteter Fehler ist aufgetreten.',
        includeStatusCode = false
    } = options;

    if (!error) return defaultMessage;

    // Handle string errors
    if (typeof error === 'string') return error;

    // Handle axios-like error structure
    const status = error?.response?.status;
    const backendError = error?.response?.data?.error || error?.response?.data?.message;
    
    if (backendError && typeof backendError === 'string') {
        const baseMessage = backendError.trim();
        if (includeStatusCode && status) {
            return `Fehler ${status}: ${baseMessage}`;
        }
        return baseMessage;
    }

    // Handle HTTP status-based messages
    if (status === 401) {
        return 'Nicht angemeldet oder Sitzung abgelaufen. Bitte erneut anmelden.';
    }
    if (status === 403) {
        return 'Zugriff verweigert. Fehlende Berechtigungen.';
    }
    if (status === 404) {
        return 'Die angeforderte Ressource wurde nicht gefunden.';
    }
    if (status === 413) {
        return 'Die Anfrage war zu groß. Bitte reduzieren Sie die Datenmenge.';
    }
    if (status === 429) {
        return 'Zu viele Anfragen. Bitte warten Sie einen Moment.';
    }
    if (status >= 500) {
        return 'Serverfehler. Bitte versuchen Sie es später erneut.';
    }

    // Handle standard Error objects
    if (error.message && typeof error.message === 'string') {
        const message = error.message.trim();
        if (includeStatusCode && status) {
            return `Fehler ${status}: ${message}`;
        }
        return message;
    }

    // Fallback to default
    return defaultMessage;
};

/**
 * Creates a standardized banner message object for user feedback.
 * 
 * @param {string} type - Banner type: 'success', 'error', 'info', 'warning'
 * @param {string} message - Message text
 * @returns {Object} Banner object { type, message }
 */
export const createBannerMessage = (type, message) => {
    const validTypes = ['success', 'error', 'info', 'warning'];
    const normalizedType = validTypes.includes(type) ? type : 'info';
    
    return {
        type: normalizedType,
        message: message || ''
    };
};

/**
 * Creates an error banner message using extractErrorMessage for consistent messaging.
 * 
 * @param {Error|Object} error - Error object
 * @param {Object} options - Options passed to extractErrorMessage
 * @returns {Object} Banner object { type: 'error', message }
 */
export const createErrorBanner = (error, options = {}) => {
    return {
        type: 'error',
        message: extractErrorMessage(error, options)
    };
};

/**
 * Creates a success banner message.
 * 
 * @param {string} message - Success message
 * @returns {Object} Banner object { type: 'success', message }
 */
export const createSuccessBanner = (message) => {
    return createBannerMessage('success', message);
};

/**
 * Logs an error consistently with context information.
 * In development, logs full error details. In production, logs minimally.
 * 
 * @param {string} context - Context where error occurred (e.g., '[useGalleryAdmin]')
 * @param {Error|Object} error - Error object
 * @param {Object} additionalInfo - Additional information to log
 */
export const logError = (context, error, additionalInfo = {}) => {
    const isDev = process.env.NODE_ENV === 'development';
    
    if (isDev) {
         
        console.error(`${context}`, error, additionalInfo);
    } else {
        // Production logging - minimal, no stack traces in console
        const message = extractErrorMessage(error);
         
        console.error(`${context}: ${message}`);
        
        // You could send to error tracking service here
        // e.g., Sentry.captureException(error, { extra: { context, ...additionalInfo } });
    }
};

/**
 * Logs a warning consistently with context information.
 * In development, logs full details. In production, logs minimal message.
 * 
 * @param {string} context - Context where warning occurred (e.g., '[flyerService]')
 * @param {string|Error} warning - Warning message or Error object
 * @param {Object} additionalInfo - Additional information to log
 */
export const logWarning = (context, warning, additionalInfo = {}) => {
    const isDev = process.env.NODE_ENV === 'development';
    
    if (isDev) {
         
        console.warn(`${context}`, warning, additionalInfo);
    } else {
        const message = typeof warning === 'string' ? warning : warning?.message || 'Warning occurred';
         
        console.warn(`${context}: ${message}`);
    }
};

/**
 * Logs informational messages (development only).
 * Silent in production to avoid exposing internal logic.
 * 
 * @param {string} context - Context where info occurred
 * @param {string} message - Informational message
 * @param {Object} additionalInfo - Additional information
 */
export const logInfo = (context, message, additionalInfo = {}) => {
    if (process.env.NODE_ENV === 'development') {
         
        console.info(`${context}: ${message}`, additionalInfo);
    }
    // Silent in production
};

/**
 * Specialized warning for missing configuration/environment variables.
 * Always logs (dev and prod) since missing config affects functionality.
 * 
 * @param {string} serviceName - Service name (e.g., 'flyerService')
 * @param {string} configKey - Configuration key that's missing
 * @param {string} instructions - Instructions for fixing
 */
export const logMissingConfig = (serviceName, configKey, instructions = '') => {
    const message = `Missing ${configKey} configuration. ${instructions}`.trim();
     
    console.warn(`[${serviceName}] ${message}`);
};

/**
 * Handles API errors consistently, extracting messages and logging.
 * Returns a banner object and logs the error.
 * 
 * @param {string} context - Context for logging
 * @param {Error|Object} error - Error object
 * @param {Object} options - Options for error handling
 * @returns {Object} Banner object for user feedback
 */
export const handleApiError = (context, error, options = {}) => {
    logError(context, error, options.additionalInfo);
    
    if (options.suppressBanner) {
        return null;
    }
    
    return createErrorBanner(error, {
        defaultMessage: options.defaultMessage,
        includeStatusCode: options.includeStatusCode
    });
};